import { UserStatusPingViewModel } from '@/models/User/UserStatusPingViewModel';
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { useMemo } from 'react';
import {
  GroupUserStatusesOnAzure,
  UserStatusPing,
} from '../../../generated-client/models';

export type StatusStoreState = {
  [groupName: string]: {
    user_statuses: {
      [userId: string]: UserStatusPingViewModel;
    };
  };
};

export const useOrgGroupUserStatusStore = create(
  subscribeWithSelector<{ groupUserStatuses: StatusStoreState }>(() => ({
    groupUserStatuses: {},
  })),
);

/**
 * Sets the group user statuses for a specific group in the store.
 * @param {string} groupName
 * @param {GroupUserStatusesOnAzure} newStatuses
 * @returns {void}
 */
export const setGroupUserStatuses = (
  groupName: string,
  newStatuses: GroupUserStatusesOnAzure,
): void => {
  const transformedStatuses = {
    ...newStatuses,
    user_statuses: Object.fromEntries(
      Object.entries(newStatuses.user_statuses || {}).map(([key, dto]) => [
        key,
        new UserStatusPingViewModel(dto),
      ]),
    ),
  };
  useOrgGroupUserStatusStore.setState((state) => ({
    groupUserStatuses: {
      ...state.groupUserStatuses,
      [groupName]: transformedStatuses,
    },
  }));
};

/**
 * Updates a single user status in the appropriate group.
 * @param userStatusDTO - The user status update
 * @param {string} [groupName] - Optional group name if known
 * @returns {boolean} - True if user was updated, false if not found
 */
export const updateUserStatus = (
  userStatusDTO: UserStatusPing,
  groupName: string,
) => {
  const userId = userStatusDTO.user_auth_id;

  // If we know the group, update directly
  if (groupName) {
    return updateUserInSpecificGroup(userId, userStatusDTO, groupName);
  }
};

/**
 * Updates a user's status in a specific group
 * @private
 * @param {string} userId - The user ID to update
 * @param {UserStatusPing} userStatusDTO - The user status update data
 * @param {string} groupName - The group name where the user exists
 * @returns {boolean} - True if update was successful
 */
const updateUserInSpecificGroup = (
  userId: string,
  userStatusDTO: UserStatusPing,
  groupName: string,
): boolean => {
  const currentState = useOrgGroupUserStatusStore.getState();
  const groupData = currentState.groupUserStatuses[groupName];

  if (!groupData) return false;

  const updatedViewModel = new UserStatusPingViewModel(userStatusDTO);

  useOrgGroupUserStatusStore.setState((state) => ({
    groupUserStatuses: {
      ...state.groupUserStatuses,
      [groupName]: {
        ...state.groupUserStatuses[groupName],
        user_statuses: {
          ...state.groupUserStatuses[groupName].user_statuses,
          [userId]: updatedViewModel,
        },
      },
    },
  }));

  return true;
};

/**
 * Hook to get group user statuses for a specific group (with selector for efficient re-renders).
 * @param {string} groupName
 * @returns {GroupUserStatuses | undefined}
 */
export const useGroupUserStatuses = (groupName: string) =>
  useOrgGroupUserStatusStore((state) => state.groupUserStatuses[groupName]);

/**
 * Hook to get all group user statuses (use sparingly as it will re-render on any group change).
 * @returns
 */
export const useAllGroupUserStatuses = () =>
  useOrgGroupUserStatusStore((state) => state.groupUserStatuses);

/**
 * Hook to get disconnected users count for a specific group.
 * @param {string} groupName
 * @returns {number}
 */
export const useDisconnectedUsersCount = (groupName: string) =>
  useOrgGroupUserStatusStore((state) => {
    const groupStatuses = state.groupUserStatuses[groupName];
    if (!groupStatuses?.user_statuses) return 0;
    return Object.values(groupStatuses.user_statuses).filter((userStatus) =>
      userStatus.isDisconnected(),
    ).length;
  });

const EMPTY_ARRAY: string[] = [];
/**
 * Hook to get disconnected users for a specific group.
 * @param {string} groupName
 * @returns {Array<string>}
 */
export const useDisconnectedUsers = (groupName: string) => {
  const groupStatuses = useOrgGroupUserStatusStore(
    (state) => state.groupUserStatuses[groupName],
  );

  const key = useMemo(() => {
    if (!groupStatuses?.user_statuses) return '';
    return Object.entries(groupStatuses.user_statuses)
      .filter(([authId, userStatus]) => userStatus.isDisconnected())
      .map(([authId]) => authId)
      .sort()
      .join(',');
  }, [groupStatuses]);

  return useMemo(() => {
    if (!groupStatuses?.user_statuses) return EMPTY_ARRAY;
    return Object.entries(groupStatuses.user_statuses)
      .filter(([authId, userStatus]) => userStatus.isDisconnected())
      .map(([authId]) => authId);
  }, [key]);
};

/**
 * Hook to get total disconnected users across all groups.
 * @returns
 */
export const useTotalDisconnectedUsers = () => {
  const allGroupStatuses = useOrgGroupUserStatusStore(
    (state) => state.groupUserStatuses,
  );

  return useMemo(
    () =>
      Object.values(allGroupStatuses).reduce((total, groupStatuses) => {
        if (!groupStatuses?.user_statuses) return total;
        return (
          total +
          Object.values(groupStatuses.user_statuses).filter((userStatus) =>
            userStatus.isDisconnected(),
          ).length
        );
      }, 0),
    [allGroupStatuses],
  );
};

/**
 * Hook to get all disconnected users across all groups.
 * @returns {Array<string>}
 */
export const useAllDisconnectedUsers = () => {
  const allGroupStatuses = useOrgGroupUserStatusStore(
    (state) => state.groupUserStatuses,
  );

  const key = useMemo(() => {
    const allDisconnected = [];
    for (const groupStatuses of Object.values(allGroupStatuses)) {
      if (!groupStatuses?.user_statuses) continue;
      const disconnected = Object.entries(groupStatuses.user_statuses)
        .filter(([authId, userStatus]) => userStatus.isDisconnected())
        .map(([authId]) => authId);
      allDisconnected.push(...disconnected);
    }
    return allDisconnected.sort().join(',');
  }, [allGroupStatuses]);

  return useMemo(() => {
    const allDisconnected = [];
    for (const groupStatuses of Object.values(allGroupStatuses)) {
      if (!groupStatuses?.user_statuses) continue;
      const disconnected = Object.entries(groupStatuses.user_statuses)
        .filter(([authId, userStatus]) => userStatus.isDisconnected())
        .map(([authId]) => authId);
      allDisconnected.push(...disconnected);
    }
    return allDisconnected;
  }, [key]);
};

// Non-hook functions for imperative access
export const getGroupUserStatuses = (groupName: string) =>
  useOrgGroupUserStatusStore.getState().groupUserStatuses[groupName];

export const getAllGroupUserStatuses = () =>
  useOrgGroupUserStatusStore.getState().groupUserStatuses;

export const getNumDisconnectedUsers = (groupName: string) => {
  const groupUserStatuses = getGroupUserStatuses(groupName);
  if (!groupUserStatuses?.user_statuses) return 0;
  return Object.values(groupUserStatuses.user_statuses).filter((userStatus) =>
    userStatus.isDisconnected(),
  ).length;
};

export const getDisconnectedUsers = (groupName: string) => {
  const groupUserStatuses = getGroupUserStatuses(groupName);
  if (!groupUserStatuses?.user_statuses) return [];
  return Object.entries(groupUserStatuses.user_statuses)
    .filter(([authId, userStatus]) => userStatus.isDisconnected())
    .map(([authId]) => authId);
};

/**
 * Hook to get a specific user's location from any group.
 * @param {string} userAuthId
 * @returns {{position: {lat: number, lng: number}, lastUpdated: number, groupName: string} | null}
 */
export const useUserLocation = (userAuthId: string) =>
  useOrgGroupUserStatusStore((state) => {
    for (const [groupName, groupUserStatuses] of Object.entries(
      state.groupUserStatuses,
    )) {
      const userStatus = groupUserStatuses?.user_statuses?.[userAuthId];
      if (userStatus?.location) {
        return {
          position: {
            lat: userStatus.location.latitude,
            lng: userStatus.location.longitude,
          },
          lastUpdated: (userStatus.location?.timestamp ?? 0) * 1000,
          groupName,
        };
      }
    }
    return null;
  });

/**
 * Hook to get a specific user's status from any group.
 * @param {string} userAuthId
 * @returns {{userStatus: UserStatusPingViewModel, groupName: string} | null}
 */
export const useUserStatus = (userAuthId: string) =>
  useOrgGroupUserStatusStore((state) => {
    for (const [groupName, groupUserStatuses] of Object.entries(
      state.groupUserStatuses,
    )) {
      const userStatus = groupUserStatuses?.user_statuses?.[userAuthId];
      if (userStatus) {
        return {
          userStatus,
          groupName,
        };
      }
    }
    return null;
  });

/**
 * Non-hook function to get a user's location from any group.
 * @param {string} userAuthId
 * @returns {{position: {lat: number, lng: number}, lastUpdated: number, groupName: string} | null}
 */
export const getUserLocation = (userAuthId: string) => {
  const allGroupStatuses = getAllGroupUserStatuses();

  for (const [groupName, groupUserStatuses] of Object.entries(
    allGroupStatuses,
  )) {
    const userStatus = groupUserStatuses?.user_statuses?.[userAuthId];
    if (userStatus?.location) {
      return {
        position: {
          lat: userStatus.location.latitude,
          lng: userStatus.location.longitude,
        },
        lastUpdated: (userStatus.location?.timestamp ?? 0) * 1000,
        groupName,
      };
    }
  }
  return null;
};

/**
 * Non-hook function to get a user's status from any group.
 * @param {string} userAuthId
 * @returns {{userStatus: UserStatusPingViewModel, groupName: string} | null}
 */
export const getUserStatus = (userAuthId: string) => {
  const allGroupStatuses = getAllGroupUserStatuses();

  for (const [groupName, groupUserStatuses] of Object.entries(
    allGroupStatuses,
  )) {
    const userStatus = groupUserStatuses?.user_statuses?.[userAuthId];
    if (userStatus) {
      return {
        userStatus,
        groupName,
      };
    }
  }
  return null;
};
